import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { notificationsApi } from '../api';
import type { UnreadCountResponse } from '../types/notifications.types';
import { QUERY_KEYS, STALE_TIMES } from './query-keys';

const NOTIFICATIONS_KEYS = {
  list: [QUERY_KEYS.NOTIFICATIONS] as const,
  unread: [QUERY_KEYS.NOTIFICATIONS_UNREAD] as const,
};

/**
 * Лента инбокса с автоподгрузкой по скроллу (курсорная пагинация).
 * Поллится, чтобы список гасился при действиях второго участника
 * (общая задача ТМ ↔ подрядчик); рефетч обновляет все загруженные страницы.
 */
export function useInfiniteNotifications(unresolvedOnly = false) {
  return useInfiniteQuery({
    // unresolvedOnly в ключе: «Все» и «Невыполненные» кешируются раздельно,
    // а префиксная инвалидация по NOTIFICATIONS накрывает оба варианта
    queryKey: [...NOTIFICATIONS_KEYS.list, { unresolvedOnly }],
    queryFn: ({ pageParam }) =>
      notificationsApi.getNotifications({
        cursor: pageParam ?? undefined,
        unresolvedOnly,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    staleTime: STALE_TIMES.REALTIME,
    refetchInterval: STALE_TIMES.REALTIME,
  });
}

/**
 * Счётчик непрочитанных для бейджа колокола. Лёгкий запрос, поллинг 30с.
 */
export function useUnreadCount(enabled = true) {
  return useQuery<UnreadCountResponse>({
    queryKey: NOTIFICATIONS_KEYS.unread,
    queryFn: () => notificationsApi.getUnreadCount(),
    staleTime: STALE_TIMES.REALTIME,
    refetchInterval: STALE_TIMES.REALTIME,
    enabled,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean }, Error, string>({
    mutationFn: (id) => notificationsApi.markNotificationRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.list });
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.unread });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; updated: number }, Error, void>({
    mutationFn: () => notificationsApi.markAllNotificationsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.list });
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_KEYS.unread });
    },
  });
}
